import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Search, X } from 'lucide-react';
import {
  searchBackofficeProjectOrganizations,
  searchBackofficeProjectOwners,
  updateBackofficeProjectAssignment,
} from '../api';
import type {
  BackofficeAssignableUserRow,
  BackofficeOrganizationSearchRow,
  BackofficeProjectDetail,
} from '../types';

interface ChangeProjectOwnershipModalProps {
  project: BackofficeProjectDetail;
  onClose: () => void;
  onUpdated: (project: BackofficeProjectDetail) => void;
}

export function ChangeProjectOwnershipModal({
  project,
  onClose,
  onUpdated,
}: ChangeProjectOwnershipModalProps) {
  const [selectedOrganization, setSelectedOrganization] = useState<BackofficeOrganizationSearchRow>(
    {
      id: project.organizationId,
      name: project.organizationName,
      slug: null,
      ownerUserId: project.ownerUserId,
      ownerName: project.ownerName,
      ownerEmail: project.ownerEmail,
    }
  );
  const [selectedOwner, setSelectedOwner] = useState<BackofficeAssignableUserRow | null>(
    project.ownerUserId
      ? {
          id: project.ownerUserId,
          name: project.ownerName,
          email: project.ownerEmail ?? '',
          organizationId: project.organizationId,
          organizationName: project.organizationName,
        }
      : null
  );
  const [organizationSearch, setOrganizationSearch] = useState('');
  const [ownerSearch, setOwnerSearch] = useState('');
  const [organizationResults, setOrganizationResults] = useState<BackofficeOrganizationSearchRow[]>(
    []
  );
  const [ownerResults, setOwnerResults] = useState<BackofficeAssignableUserRow[]>([]);
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const selectedOrganizationId = selectedOrganization.id;
  const canSubmit =
    !submitting &&
    !!selectedOrganizationId &&
    !!selectedOwner &&
    (selectedOrganizationId !== project.organizationId || selectedOwner.id !== project.ownerUserId);

  const ownerSummary = useMemo(() => {
    if (!selectedOwner) {
      return 'No owner selected';
    }
    return selectedOwner.name ?? selectedOwner.email;
  }, [selectedOwner]);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const trimmed = organizationSearch.trim();
    if (trimmed.length < 2) {
      setOrganizationResults([]);
      setOrganizationLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      setOrganizationLoading(true);
      searchBackofficeProjectOrganizations(trimmed, controller.signal)
        .then((response) => setOrganizationResults(response.organizations))
        .catch((caught: unknown) => {
          if (caught instanceof DOMException && caught.name === 'AbortError') {
            return;
          }
          setError(caught instanceof Error ? caught.message : 'Failed to search organizations');
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setOrganizationLoading(false);
          }
        });
    }, 250);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [organizationSearch]);

  useEffect(() => {
    const trimmed = ownerSearch.trim();
    if (trimmed.length < 2 || !selectedOrganizationId) {
      setOwnerResults([]);
      setOwnerLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      setOwnerLoading(true);
      searchBackofficeProjectOwners(selectedOrganizationId, trimmed, controller.signal)
        .then((response) => setOwnerResults(response.users))
        .catch((caught: unknown) => {
          if (caught instanceof DOMException && caught.name === 'AbortError') {
            return;
          }
          setError(
            caught instanceof Error ? caught.message : 'Failed to search organization members'
          );
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setOwnerLoading(false);
          }
        });
    }, 250);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [ownerSearch, selectedOrganizationId]);

  function handleOrganizationSelect(organization: BackofficeOrganizationSearchRow) {
    setSelectedOrganization(organization);
    setOrganizationSearch('');
    setOrganizationResults([]);
    setError(null);

    if (selectedOwner?.organizationId !== organization.id) {
      setSelectedOwner(null);
      setOwnerSearch('');
      setOwnerResults([]);
    }
  }

  function handleOwnerSelect(user: BackofficeAssignableUserRow) {
    setSelectedOwner(user);
    setOwnerSearch('');
    setOwnerResults([]);
    setError(null);
  }

  async function handleSubmit() {
    if (!selectedOwner) {
      setError('Select an owner from the chosen organization.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await updateBackofficeProjectAssignment(project.id, {
        organizationId: selectedOrganization.id,
        ownerUserId: selectedOwner.id,
      });
      onUpdated(response.project);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : 'Failed to update study ownership');
    } finally {
      setSubmitting(false);
    }
  }

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-project-ownership-title"
        className="max-h-[92vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <div className="border-b border-neutral-200 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2
                id="change-project-ownership-title"
                className="font-inter-tight text-xl font-semibold text-neutral-900"
              >
                Change owner/organization
              </h2>
              <p className="mt-1 text-small text-neutral-500">{project.title}</p>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900"
              aria-label="Close"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>

        <div className="max-h-[calc(92vh-160px)] space-y-6 overflow-y-auto px-6 py-5">
          {error ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-small text-red-800">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <section className="space-y-3">
            <div>
              <div className="text-small font-medium text-neutral-700">Organization</div>
              <div className="mt-1 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
                <div className="font-medium text-neutral-900">{selectedOrganization.name}</div>
              </div>
            </div>

            <label className="block">
              <span className="text-small font-medium text-neutral-700">
                Search organization by name
              </span>
              <div className="mt-1 flex items-center gap-2 rounded-lg border border-neutral-300 px-3 py-2">
                <Search className="size-4 text-neutral-400" />
                <input
                  value={organizationSearch}
                  onChange={(event) => setOrganizationSearch(event.target.value)}
                  className="min-w-0 flex-1 border-0 text-body outline-none"
                  placeholder="Start typing an organization name"
                />
              </div>
            </label>

            {organizationSearch.trim().length >= 2 ? (
              <div className="rounded-lg border border-neutral-200">
                {organizationLoading ? (
                  <div className="px-4 py-3 text-small text-neutral-500">
                    Searching organizations
                  </div>
                ) : organizationResults.length > 0 ? (
                  organizationResults.map((organization) => (
                    <button
                      key={organization.id}
                      type="button"
                      onClick={() => handleOrganizationSelect(organization)}
                      className="flex w-full items-center justify-between border-b border-neutral-100 px-4 py-3 text-left last:border-b-0 hover:bg-backoffice-50"
                    >
                      <span>
                        <span className="block text-small font-medium text-neutral-900">
                          {organization.name}
                        </span>
                        <span className="block text-xs text-neutral-500">
                          {organization.ownerName ?? organization.ownerEmail ?? 'No owner assigned'}
                        </span>
                      </span>
                      <span className="text-xs text-neutral-500">
                        {organization.id === selectedOrganization.id ? 'Selected' : 'Choose'}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-small text-neutral-500">
                    No organizations found
                  </div>
                )}
              </div>
            ) : null}
          </section>

          <section className="space-y-3">
            <div>
              <div className="text-small font-medium text-neutral-700">Owner</div>
              <div className="mt-1 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
                <div className="font-medium text-neutral-900">{ownerSummary}</div>
                {selectedOwner?.email ? (
                  <div className="mt-1 text-xs text-neutral-500">{selectedOwner.email}</div>
                ) : null}
              </div>
            </div>

            <label className="block">
              <span className="text-small font-medium text-neutral-700">
                Search a member in the selected organization
              </span>
              <div className="mt-1 flex items-center gap-2 rounded-lg border border-neutral-300 px-3 py-2">
                <Search className="size-4 text-neutral-400" />
                <input
                  value={ownerSearch}
                  onChange={(event) => setOwnerSearch(event.target.value)}
                  className="min-w-0 flex-1 border-0 text-body outline-none"
                  placeholder="Start typing a member name or email"
                />
              </div>
            </label>

            {ownerSearch.trim().length >= 2 ? (
              <div className="rounded-lg border border-neutral-200">
                {ownerLoading ? (
                  <div className="px-4 py-3 text-small text-neutral-500">Searching members</div>
                ) : ownerResults.length > 0 ? (
                  ownerResults.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleOwnerSelect(user)}
                      className="flex w-full items-center justify-between border-b border-neutral-100 px-4 py-3 text-left last:border-b-0 hover:bg-backoffice-50"
                    >
                      <span>
                        <span className="block text-small font-medium text-neutral-900">
                          {user.name ?? user.email}
                        </span>
                        <span className="block text-xs text-neutral-500">{user.email}</span>
                      </span>
                      <span className="text-xs text-neutral-500">
                        {selectedOwner?.id === user.id ? 'Selected' : 'Choose'}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-small text-neutral-500">No members found</div>
                )}
              </div>
            ) : null}
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-neutral-200 px-4 py-2 text-small font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="rounded-lg bg-backoffice-500 px-4 py-2 text-small font-semibold text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Saving changes' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
